import type { Restaurant } from "@/types";
import { compositeScore20, tasteFineScore, tasteGrade } from "@/lib/rating";
import { estimatePriceValue } from "@/lib/utils";

export type RankingItem = { restaurant: Restaurant; score: number; note?: string };
export type RankingDef = {
  key: string;
  title: string;
  description: string;
  limit?: number;
  compute: (list: Restaurant[]) => RankingItem[];
};

function byScoreDesc<T extends RankingItem>(a: T, b: T) { return (b.score - a.score) || a.restaurant.name.localeCompare(b.restaurant.name, 'ja'); }

// 現在のデータで作れるランキング定義
export const baseRankingDefs: RankingDef[] = [
  {
    key: 'overall',
    title: '総合ランキング',
    description: '総合点（0–20）の高い順',
    compute(list) {
      return list
        .filter(r => !r.isWishlist)
        .map(r => ({ restaurant: r, score: compositeScore20(r) }))
        .filter(x => x.score > 0)
        .sort(byScoreDesc);
    }
  },
  {
    key: 'taste',
    title: '味ランキング',
    description: '味の細分スコア（9段階）順',
    compute(list) {
      return list
        .filter(r => !r.isWishlist)
        .map(r => {
          const score = tasteFineScore(r);
          const grade = tasteGrade(r);
          const note = grade ? `グレード ${grade}` : undefined;
          return { restaurant: r, score, note };
        })
        .filter(x => x.score > 0)
        .sort(byScoreDesc);
    }
  },
  {
    key: 'ambience',
    title: '雰囲気ランキング',
    description: '雰囲気評価の高い順',
    compute(list) {
      return list.filter(r => !r.isWishlist && (r.ratingAmbience||0) > 0)
        .map(r => ({ restaurant: r, score: r.ratingAmbience as number }))
        .sort(byScoreDesc);
    }
  },
  {
    key: 'service',
    title: '接客ランキング',
    description: '接客評価の高い順',
    compute(list) {
      return list.filter(r => !r.isWishlist && (r.ratingService||0) > 0)
        .map(r => ({ restaurant: r, score: r.ratingService as number }))
        .sort(byScoreDesc);
    }
  },
  {
    key: 'cospa',
    title: 'コスパランキング',
    description: '価格満足度（コスパ）の高い順。総合点で同点調整。',
    compute(list) {
      return list.filter(r => !r.isWishlist && (r.ratingCost||0) > 0)
        .map(r => ({ restaurant: r, score: (r.ratingCost as number) + compositeScore20(r)/100 }))
        .sort(byScoreDesc);
    }
  },
  {
    key: 'ramen',
    title: 'ラーメン店ランキング',
    description: '「ラーメン」ジャンルの総合点順',
    compute(list) {
      return list.filter(r => !r.isWishlist && /ラーメン/i.test(r.cuisine||''))
        .map(r => ({ restaurant: r, score: compositeScore20(r) }))
        .sort(byScoreDesc);
    }
  },
  {
    key: 'cheap-good',
    title: '安ウマランキング',
    description: '推定価格が安く（目安）、総合点が高い順（簡易スコア）',
    compute(list) {
      return list.filter(r => !r.isWishlist)
        .map(r => {
          const price = estimatePriceValue(r.priceRange) ?? 2000; // 円
          const total = compositeScore20(r);
          const score = total - Math.min(4000, price)/1000; // 大雑把なバランス
          return { restaurant: r, score, note: `総合${total}点 / 目安¥${price}` };
        })
        .filter(x => compositeScore20(x.restaurant) > 0)
        .sort(byScoreDesc);
    }
  },
];

// URL キー生成: 日本語など非ASCIIは衝突しないようエンコード
function slugify(input: string): string {
  const s = String(input).trim();
  const ascii = s
    .normalize('NFKC')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-]/g, '');
  if (ascii.length >= 2) return ascii;
  try {
    // URL セーフにする（/ は含まれない）。先頭に u- を付けて区別。
    return `u-${encodeURIComponent(s)}`;
  } catch {
    // 念のためのフォールバック（UTF-16 コードポイントの16進連結）
    return 'u-' + Array.from(s).map(ch => ch.charCodeAt(0).toString(16)).join('');
  }
}

export function buildAllRankingDefs(list: Restaurant[]): RankingDef[] {
  const defs: RankingDef[] = baseRankingDefs.slice();
  // 動的: 料理種別ごと（5店舗以上）
  const map = new Map<string, number>();
  for (const r of list) {
    if (r.isWishlist) continue;
    const k = (r.cuisine || '').trim();
    if (!k) continue;
    map.set(k, (map.get(k) || 0) + 1);
  }
  for (const [cuisine, count] of map.entries()) {
    if (count < 5) continue;
    const slug = slugify(cuisine);
    defs.push({
      key: `cuisine-${slug}`,
      title: `${cuisine}ランキング`,
      description: `「${cuisine}」の総合点が高い順`,
      compute(list) {
        return list.filter(r => !r.isWishlist && (r.cuisine||'').trim() === cuisine)
          .map(r => ({ restaurant: r, score: compositeScore20(r) }))
          .sort(byScoreDesc);
      }
    });
  }

  // 追加の複合指標ランキング
  defs.push(
    {
      key: 'taste-cospa-best',
      title: '味×コスパ最強',
      description: '味重視＋コスパ満足度＋控えめ価格の複合スコア',
      compute(list) {
        return list.filter(r => !r.isWishlist)
          .map(r => {
            const taste = tasteFineScore(r);
            const costSat = Number(r.ratingCost)||0; // 満足度
            const price = Math.min(5000, Math.max(500, (estimatePriceValue(r.priceRange) ?? 2000)));
            const score = taste*1.2 + costSat + (3000 - price)/1000 + compositeScore20(r)/20;
            const grade = tasteGrade(r);
            const note = grade ? `味 ${grade}` : undefined;
            return { restaurant: r, score, note };
          })
          .sort(byScoreDesc);
      }
    },
    {
      key: 'date-night',
      title: 'デート向きランキング',
      description: '雰囲気×2 + 接客 + 総合の複合スコア',
      compute(list) {
        return list.filter(r => !r.isWishlist)
          .map(r => {
            const amb = Number(r.ratingAmbience)||0;
            const svc = Number(r.ratingService)||0;
            const score = amb*2 + svc + compositeScore20(r)/10;
            return { restaurant: r, score };
          })
          .sort(byScoreDesc);
      }
    },
    {
      key: 'easy-going',
      title: 'ゆる飲み安定ランキング',
      description: '接客 + コスパ満足度 − 価格 + 総合の複合スコア',
      compute(list) {
        return list.filter(r => !r.isWishlist)
          .map(r => {
            const svc = Number(r.ratingService)||0;
            const cost = Number(r.ratingCost)||0;
            const price = estimatePriceValue(r.priceRange) ?? 2000;
            const score = svc + cost + (3000 - Math.min(5000, price))/1200 + compositeScore20(r)/20;
            return { restaurant: r, score };
          })
          .sort(byScoreDesc);
      }
    }
  );

  return defs;
}

export function getRankingDefFromList(list: Restaurant[], key: string): RankingDef | undefined {
  const defs = buildAllRankingDefs(list);
  const direct = defs.find(d => d.key === key);
  if (direct) return direct;
  // React Router は URL デコード後のパラメータを返すため、
  // encodeURIComponent ベースのキーは再エンコードして照合する
  if (key.startsWith('cuisine-u-')) {
    const tail = key.slice('cuisine-u-'.length);
    const recoded = 'cuisine-u-' + encodeURIComponent(tail);
    const hit = defs.find(d => d.key === recoded);
    if (hit) return hit;
  }
  return undefined;
}
